import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text} from '@mantine/core';
import React from 'react';
import {Node, NodeProps} from 'reactflow';

import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE = 'procedureRunDesignFlowchartColumnNode';

export const FALLBACK_COLUMN_NODE_WIDTH = COMMON_NODE_WIDTH;
export const FALLBACK_COLUMN_NODE_HEIGHT = 52; // best-effort static fallback; see TODO (BNCH-156222)
export interface ProcedureRunDesignFlowchartColumnNodeData {
  methodName: string;
  factorsSet: number;
  shouldHighlight: boolean;
}

interface ColumnHeaderProps {
  methodName: string;
  factorsSet: number;
  shouldHighlight: boolean;
}

export function createProcedureRunDesignFlowchartColumnNode({
  methodId,
  methodName,
  factorsSet,
  shouldHighlight,
}: {
  methodId: string;
  methodName: string;
  factorsSet: number;
  shouldHighlight: boolean;
}): Node<ProcedureRunDesignFlowchartColumnNodeData> {
  return {
    id: `column-${methodId}`,
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
    style: {
      width: FALLBACK_COLUMN_NODE_WIDTH,
    },
    position: {
      x: 0,
      y: 0,
    },
    data: {
      methodName,
      factorsSet,
      shouldHighlight,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

function getFactorsSetText(factorsSet: number): string {
  if (factorsSet === 0) {
    return 'No factors set';
  }
  return `${factorsSet} ${factorsSet === 1 ? 'factor' : 'factors'}`;
}

function ProcedureRunDesignFlowchartColumnHeader({
  methodName,
  factorsSet,
  shouldHighlight,
}: ColumnHeaderProps): JSX.Element {
  const {classes, cx} = useHeaderStyles();
  const factorsSetText = getFactorsSetText(factorsSet);
  return (
    <div className={classes.container}>
      <Text
        className={cx(classes.methodName, shouldHighlight && classes.methodNameHighlighted)}
        title={methodName}
      >
        {methodName}
      </Text>
      <Chip
        className={cx(classes.chip, factorsSet === 0 && classes.chipNoFactors)}
        size={ChipSize.SMALL}
        isReadonly={true}
        text={factorsSetText}
      />
    </div>
  );
}

export default function ProcedureRunDesignFlowchartColumnNode(
  props: NodeProps<ProcedureRunDesignFlowchartColumnNodeData>
): JSX.Element {
  const {methodName, factorsSet, shouldHighlight} = props.data;
  const {classes} = useColumnNodeStyles();
  return (
    <div className={classes.container}>
      <ProcedureRunDesignFlowchartColumnHeader
        methodName={methodName}
        factorsSet={factorsSet}
        shouldHighlight={shouldHighlight}
      />
    </div>
  );
}

const useColumnNodeStyles = createStyles(() => ({
  container: {
    width: '100%',
    height: '100%',
  },
}));

const useHeaderStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  methodName: {
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: 1,
    color: Color.GRAY_900,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  methodNameHighlighted: {
    color: theme.colors.blue[6],
  },
  chip: {
    '&.chip, &.chip:hover, &.chip:active': {
      cursor: 'default',
      margin: 0,
    },
  },
  chipNoFactors: {
    '&.chip, &.chip:hover, &.chip:focus, &.chip:focus-visible, &.chip:active': {
      backgroundColor: Color.BLUE_100,
      borderColor: Color.BLUE_100,
      color: Color.BLUE_400,
    },
  },
}));
